import { Box, type SxProps, type Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC, ReactNode } from "react";
import { ALPHA } from "../../../theme/tokens";
import { HoverCard } from "../../UIToolkit/Popups/HoverCard";

/**
 * A name that is not a link but has a card behind it: a quiet dotted underline says "there is more here"
 * without dressing it as a link it is not.
 */
const NAME_SX: SxProps<Theme> = (theme) => ({
	font: "inherit",
	cursor: "help",
	borderRadius: 0.5,
	textDecorationLine: "underline",
	textDecorationStyle: "dotted",
	textDecorationColor: alpha(theme.palette.primary.main, ALPHA.CUE_UNDERLINE),
	textUnderlineOffset: "0.2em",
	"&:focus-visible": { outline: "none", boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, ALPHA.FOCUS_RING)}` },
});

type Props = {
	label: ReactNode;
	/** The card's content (a `RuleCard`), mounted only while it is open. */
	card: ReactNode;
	dataTestId: string;
};

/**
 * A breakdown name shown without a link — a segment's name, an overtime rule from another department, a
 * rule for a viewer who cannot open Wage Settings — that still opens its hover card. A tab stop of its
 * own, so the card is reachable from the keyboard as well as the pointer.
 */
export const RuleNameText: FC<Props> = ({ label, card, dataTestId }) => (
	<HoverCard content={card}>
		<Box component="span" tabIndex={0} data-testid={dataTestId} sx={NAME_SX}>
			{label}
		</Box>
	</HoverCard>
);
